import { getCurrentUserSessionAccessToken, saveUserDetails } from '@/storage/local-storage'
import { fetchAuthSession } from 'aws-amplify/auth'
import { jwtDecode } from 'jwt-decode'

let refreshTokenPromise = null
/**
 * Fetches data from the server with authorization.
 * @param {Object} options - The options for the fetch request.
 * @param {string} options.path - The path of the endpoint to fetch from.
 * @param {string} [options.method='GET'] - The HTTP method for the request.
 * @param {Object} [options.body] - The request body data (if applicable).
 * @returns {Promise<Object>} A promise that resolves to the parsed JSON response.
 * @throws {Error} Throws an error if the response status is not ok.
 */
async function performFetch({ path, method = 'GET', body, accessToken }) {
  const response = await fetch(import.meta.env.VITE_SERVER_DOMAIN + path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body,
  })

  // Parse JSON once
  let data
  try {
    data = await response.json()
  } catch (_) {
    data = undefined
  }

  if (!response.ok) {
    const msg = (data && (data.message || data.error)) || response.statusText || 'Request failed'
    const err = new Error(msg)
    err.status = response.status
    err.data = data
    throw err
  }

  return data
}
export default async function fetchWithAuthorization({ path, method = 'GET', body }) {
  let accessToken = getCurrentUserSessionAccessToken()
  const decodedToken = jwtDecode(accessToken)
  const expiry = decodedToken.exp * 1000

  // Check if the token is about to expire
  if (expiry - new Date().getTime() < 5 * 60 * 1000) {
    if (!refreshTokenPromise) {
      // If no token refresh is in progress, start a new one
      refreshTokenPromise = fetchAuthSession({ forceRefresh: true })
        .then(session => {
          accessToken = session.tokens?.accessToken.toString()
          saveUserDetails(session) // Save the new session data
          return accessToken
        })
        .finally(() => {
          refreshTokenPromise = null // Clear the promise once refresh is done
        })
    }
    // Wait for the token refresh to complete
    accessToken = await refreshTokenPromise
  }

  return await performFetch({ path, method, body, accessToken })
}
